import http from '../http'

/**
 * 获取所有角色列表
 * @returns {Promise} 返回所有有效的角色列表
 */
export function getAllRoles() {
  return http.get('/roles')
}

/**
 * 分页查询角色列表
 * @param {Object} params - 查询参数
 * @param {Number} [params.page] - 页码（从0开始），默认0
 * @param {Number} [params.size] - 每页数量，默认10
 * @param {String} [params.sortBy] - 排序字段，默认id
 * @param {String} [params.sortDir] - 排序方向（asc/desc），默认desc
 * @param {String} [params.roleName] - 角色名称（模糊查询）
 * @param {String} [params.description] - 角色描述（模糊查询）
 * @param {Number} [params.creatorId] - 创建人ID
 * @returns {Promise} 返回角色列表和分页信息
 */
export function query_roles_pagination(params = {}) {
  const queryParams = {
    page: params.page !== undefined ? params.page : 0,
    size: params.size || 10,
    sortBy: params.sortBy || 'id',
    sortDir: params.sortDir || 'desc',
    ...params
  }
  
  // 移除undefined值
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key]
    }
  })
  
  // 如果有搜索条件，使用 /roles/search/page 接口
  if (queryParams.roleName || queryParams.description || queryParams.creatorId) {
    return http.get('/roles/search/page', { params: queryParams })
  }
  
  // 否则使用 /roles/page 接口
  return http.get('/roles/page', { params: queryParams })
}

/**
 * 根据ID查询角色
 * @param {Number|String} id - 角色ID
 * @returns {Promise} 返回角色详细信息
 */
export function get_role_by_id(id) {
  return http.get(`/roles/${id}`)
}

/**
 * 创建角色
 * @param {Object} data - 角色数据
 * @param {String} data.roleName - 角色名称（必填）
 * @param {String} [data.description] - 角色描述
 * @returns {Promise} 返回创建的角色信息
 */
export function create_role(data) {
  return http.post('/roles', data)
}

/**
 * 更新角色
 * @param {Number|String} id - 角色ID
 * @param {Object} data - 角色数据（所有字段可选）
 * @param {String} [data.roleName] - 角色名称
 * @param {String} [data.description] - 角色描述
 * @returns {Promise} 返回更新后的角色信息
 */
export function update_role(id, data) {
  return http.put(`/roles/${id}`, data)
}

/**
 * 删除角色
 * @param {Number|String} id - 角色ID
 * @returns {Promise} 返回删除结果
 * @description 执行软删除，如果角色已被用户使用，无法删除
 */
export function delete_role(id) {
  return http.delete(`/roles/${id}`)
}

/**
 * 为角色分配权限
 * @param {Object} data - 分配数据
 * @param {Number} data.roleId - 角色ID
 * @param {Array<Number>} data.permissionIds - 权限ID列表
 * @returns {Promise} 返回分配结果
 * @description 此操作会先删除该角色的所有现有权限关联，然后创建新的关联
 * @note 根据PERMISSION_API.md，应使用 POST /permissions/assign 接口
 */
export function assign_permissions_to_role(data) {
  return http.post('/permissions/assign', data)
}

/**
 * 检查角色名称是否存在
 * @param {String} roleName - 角色名称
 * @returns {Promise} 返回true表示已存在，false表示不存在
 */
export function check_role_name(roleName) {
  return http.get('/roles/check-name', { params: { roleName } })
}

/**
 * 根据角色名称获取角色
 * @param {String} roleName - 角色名称
 * @returns {Promise} 返回角色详细信息
 */
export function get_role_by_name(roleName) {
  return http.get(`/roles/name/${roleName}`)
}

export default {
  getAllRoles,
  query_roles_pagination,
  get_role_by_id,
  create_role,
  update_role,
  delete_role,
  assign_permissions_to_role,
  check_role_name,
  get_role_by_name
}
